
import React from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { 
  Battery, 
  Signal, 
  Zap,
  AlertTriangle
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getStatusIcon, getStatusBadgeClass } from '../VoltageRegulatorStatusHelpers';

interface VoltageRegulator {
  deviceId: string;
  location: string;
  status: string;
  batteryLevel: number;
  signalStrength: number;
  lastCommunication: string;
  firmwareVersion: string;
  readings: {
    inputVoltage: number;
    outputVoltage: number;
    current: number;
    frequency: number;
    regulationAccuracy: number;
    responseTime: number;
  };
  maintenanceStatus: {
    lastMaintenance: string;
    nextScheduled: string;
    alerts: string[];
  };
}

interface VoltageRegulatorTableRowProps {
  regulator: VoltageRegulator;
  onSelectDevice: (deviceId: string) => void;
}

const VoltageRegulatorTableRow: React.FC<VoltageRegulatorTableRowProps> = ({ regulator, onSelectDevice }) => {
  return (
    <TableRow 
      key={regulator.deviceId}
      className="cursor-pointer hover:bg-muted/80"
      onClick={() => onSelectDevice(regulator.deviceId)}
    >
      <TableCell className="font-medium">{regulator.deviceId}</TableCell>
      <TableCell>{regulator.location}</TableCell>
      <TableCell>
        <div className="flex items-center">
          {getStatusIcon(regulator.status)}
          <Badge className={cn("ml-2 text-xs", getStatusBadgeClass(regulator.status))}>
            {regulator.status}
          </Badge>
        </div>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="flex items-center">
              <Battery className={cn(
                "h-4 w-4 mr-2",
                regulator.batteryLevel > 70 ? "text-green-500" :
                regulator.batteryLevel > 30 ? "text-amber-500" : "text-red-500"
              )} />
              {regulator.batteryLevel}%
            </TooltipTrigger>
            <TooltipContent>
              <p>Battery Level: {regulator.batteryLevel}%</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="flex items-center">
              <Signal className={cn(
                "h-4 w-4 mr-2",
                regulator.signalStrength > 70 ? "text-green-500" :
                regulator.signalStrength > 30 ? "text-amber-500" : "text-red-500"
              )} />
              {regulator.signalStrength}%
            </TooltipTrigger>
            <TooltipContent>
              <p>Signal Strength: {regulator.signalStrength}%</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="flex items-center">
              <Zap className={cn(
                "h-4 w-4 mr-2",
                regulator.readings.regulationAccuracy > 95 ? "text-green-500" :
                regulator.readings.regulationAccuracy > 90 ? "text-amber-500" : "text-red-500"
              )} />
              {regulator.readings.regulationAccuracy}%
            </TooltipTrigger>
            <TooltipContent>
              <p>Regulation Accuracy: {regulator.readings.regulationAccuracy}%</p>
              <p>Input: {regulator.readings.inputVoltage}V</p>
              <p>Output: {regulator.readings.outputVoltage}V</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        {regulator.maintenanceStatus.alerts.length > 0 ? (
          <Badge variant="outline" className="text-xs text-red-500 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            {regulator.maintenanceStatus.alerts.length} {regulator.maintenanceStatus.alerts.length === 1 ? 'Alert' : 'Alerts'}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs text-green-500">
            No Alerts
          </Badge>
        )}
      </TableCell>
    </TableRow>
  );
};

export default VoltageRegulatorTableRow;
